/**
 * 🛡️ G2GENTE - Questionário DISC com Escala Likert
 * - Mostra resultado existente se disponível
 * - Gráfico radar (teia de aranha) do perfil DISC
 * - Reavaliação permitida apenas após 7 dias
 * - 20 perguntas, escala 1-5
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { candidatesAPI, testInvitesAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft,
  Loader2,
  Brain,
  ChevronLeft,
  ChevronRight,
  Target,
  Users,
  Heart,
  Shield,
  RefreshCw,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

// 20 perguntas DISC com escala Likert
const DISC_QUESTIONS = [
  // Dominância (D) - 5 perguntas
  { id: 1, text: "Gosto de assumir a liderança em projetos de equipe.", trait: "D" },
  { id: 2, text: "Prefiro tomar decisões rapidamente em vez de esperar.", trait: "D" },
  { id: 3, text: "Sou competitivo e gosto de vencer desafios.", trait: "D" },
  { id: 4, text: "Vou direto ao ponto em conversas e reuniões.", trait: "D" },
  { id: 5, text: "Sinto-me confortável confrontando problemas de frente.", trait: "D" },
  
  // Influência (I) - 5 perguntas
  { id: 6, text: "Gosto de conhecer novas pessoas e fazer networking.", trait: "I" },
  { id: 7, text: "Sou otimista e consigo motivar os outros facilmente.", trait: "I" },
  { id: 8, text: "Prefiro trabalhar em equipe do que sozinho.", trait: "I" },
  { id: 9, text: "Sou expressivo e demonstro minhas emoções abertamente.", trait: "I" },
  { id: 10, text: "Gosto de ambientes de trabalho descontraídos e divertidos.", trait: "I" },
  
  // Estabilidade (S) - 5 perguntas
  { id: 11, text: "Prefiro rotinas previsíveis a mudanças constantes.", trait: "S" },
  { id: 12, text: "Sou paciente e sei esperar o momento certo.", trait: "S" },
  { id: 13, text: "Valorizo a harmonia e evito conflitos desnecessários.", trait: "S" },
  { id: 14, text: "Sou leal às pessoas e empresas em que confio.", trait: "S" },
  { id: 15, text: "Prefiro ouvir mais do que falar em discussões.", trait: "S" },
  
  // Conformidade (C) - 5 perguntas
  { id: 16, text: "Analiso os detalhes antes de tomar decisões importantes.", trait: "C" },
  { id: 17, text: "Busco sempre a precisão e qualidade no meu trabalho.", trait: "C" },
  { id: 18, text: "Prefiro seguir processos e métodos estabelecidos.", trait: "C" },
  { id: 19, text: "Sou organizado e gosto de planejar antecipadamente.", trait: "C" },
  { id: 20, text: "Questiono informações e busco dados para validar ideias.", trait: "C" }
];

const LIKERT_SCALE = [
  { value: 1, label: "Discordo totalmente" },
  { value: 2, label: "Discordo" },
  { value: 3, label: "Neutro" },
  { value: 4, label: "Concordo" },
  { value: 5, label: "Concordo totalmente" }
];

const DISC_PROFILES = {
  D: {
    name: "D - Dominância",
    title: "Determinado",
    icon: Target,
    color: "text-red-500",
    bgColor: "bg-red-100",
    chartColor: "#ef4444",
    description: "Você é orientado a resultados, direto e decisivo. Gosta de desafios e toma ações rápidas para alcançar objetivos.",
    strengths: ["Liderança", "Foco em resultados", "Tomada de decisão", "Resolução de problemas"],
    attention: ["Impaciente às vezes", "Pode ser muito direto"],
    tips: "Lembre-se de ouvir os outros e considerar diferentes perspectivas."
  },
  I: {
    name: "I - Influência",
    title: "Inspirador",
    icon: Users,
    color: "text-amber-500",
    bgColor: "bg-amber-100",
    chartColor: "#f59e0b",
    description: "Você é entusiasta, otimista e colaborativo. Gosta de trabalhar com pessoas e criar um ambiente positivo.",
    strengths: ["Comunicação", "Entusiasmo", "Criatividade", "Trabalho em equipe"],
    attention: ["Pode perder foco", "Otimismo excessivo às vezes"],
    tips: "Mantenha o foco nas tarefas e prazos para maximizar sua eficácia."
  },
  S: {
    name: "S - Estabilidade",
    title: "Solidário",
    icon: Heart,
    color: "text-emerald-500",
    bgColor: "bg-emerald-100",
    chartColor: "#10b981",
    description: "Você é paciente, confiável e cooperativo. Valoriza a harmonia e oferece suporte consistente à equipe.",
    strengths: ["Paciência", "Confiabilidade", "Escuta ativa", "Colaboração"],
    attention: ["Resistência a mudanças", "Dificuldade em dizer não"],
    tips: "Não tenha medo de expressar suas opiniões e necessidades."
  },
  C: {
    name: "C - Conformidade",
    title: "Consciencioso",
    icon: Shield,
    color: "text-blue-500",
    bgColor: "bg-blue-100",
    chartColor: "#3b82f6",
    description: "Você é analítico, preciso e focado em qualidade. Valoriza a precisão e segue padrões elevados.",
    strengths: ["Análise", "Precisão", "Organização", "Qualidade"],
    attention: ["Perfeccionismo", "Análise excessiva às vezes"],
    tips: "Aceite que nem tudo precisa ser perfeito para funcionar bem."
  }
};

// Componente do Gráfico Radar (Teia de Aranha)
function DISCRadarChart({ scores }) {
  const maxScore = 25; // 5 perguntas * 5 pontos
  const traits = ['D', 'I', 'S', 'C'];
  const labels = ['Dominância', 'Influência', 'Estabilidade', 'Conformidade'];
  const colors = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6'];
  
  // Calcular pontos para o polígono
  const centerX = 150;
  const centerY = 150;
  const radius = 100;
  
  // Calcular percentuais normalizados (0-1)
  const normalizedScores = traits.map(t => (scores[t] || 0) / maxScore);
  
  // Calcular pontos do polígono
  const points = normalizedScores.map((score, i) => {
    const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
    const x = centerX + Math.cos(angle) * radius * score;
    const y = centerY + Math.sin(angle) * radius * score;
    return `${x},${y}`;
  }).join(' ');
  
  // Linhas de grid (círculos)
  const gridLevels = [0.25, 0.5, 0.75, 1];
  
  return (
    <div className="w-full flex justify-center" data-testid="disc-radar-chart">
      <svg viewBox="0 0 300 300" className="w-full max-w-[280px]">
        {/* Grid circular */}
        {gridLevels.map((level, i) => (
          <polygon
            key={i}
            points={traits.map((_, j) => {
              const angle = (Math.PI * 2 * j) / traits.length - Math.PI / 2;
              const x = centerX + Math.cos(angle) * radius * level;
              const y = centerY + Math.sin(angle) * radius * level;
              return `${x},${y}`;
            }).join(' ')}
            fill="none"
            stroke="#e2e8f0"
            strokeWidth="1"
          />
        ))}
        
        {/* Linhas dos eixos */}
        {traits.map((_, i) => {
          const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
          const x = centerX + Math.cos(angle) * radius;
          const y = centerY + Math.sin(angle) * radius;
          return (
            <line
              key={i}
              x1={centerX}
              y1={centerY}
              x2={x}
              y2={y}
              stroke="#cbd5e1"
              strokeWidth="1"
            />
          );
        })}
        
        {/* Área preenchida */}
        <polygon
          points={points}
          fill="rgba(8, 145, 178, 0.3)"
          stroke="#0891b2"
          strokeWidth="2"
        />
        
        {/* Pontos nos vértices */}
        {normalizedScores.map((score, i) => {
          const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
          const x = centerX + Math.cos(angle) * radius * score;
          const y = centerY + Math.sin(angle) * radius * score;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="6"
              fill={colors[i]}
              stroke="white"
              strokeWidth="2"
            />
          );
        })}
        
        {/* Labels */}
        {labels.map((label, i) => {
          const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
          const labelRadius = radius + 30;
          const x = centerX + Math.cos(angle) * labelRadius;
          const y = centerY + Math.sin(angle) * labelRadius;
          const percentage = Math.round(normalizedScores[i] * 100);
          
          return (
            <g key={i}>
              <text
                x={x}
                y={y - 8}
                textAnchor="middle"
                className="text-xs font-semibold"
                fill={colors[i]}
              >
                {traits[i]}
              </text>
              <text
                x={x}
                y={y + 8}
                textAnchor="middle"
                className="text-xs"
                fill="#64748b"
              >
                {percentage}%
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// Componente de barras de comparação DISC
function DISCBars({ scores }) {
  const maxScore = 25;
  const traits = [
    { key: 'D', label: 'Dominância', color: 'bg-red-500' },
    { key: 'I', label: 'Influência', color: 'bg-amber-500' },
    { key: 'S', label: 'Estabilidade', color: 'bg-emerald-500' },
    { key: 'C', label: 'Conformidade', color: 'bg-blue-500' }
  ];
  
  return (
    <div className="space-y-3" data-testid="disc-bars">
      {traits.map(({ key, label, color }) => {
        const percentage = Math.round(((scores[key] || 0) / maxScore) * 100);
        return (
          <div key={key}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-600">{label}</span>
              <span className="font-semibold text-slate-900">{percentage}%</span>
            </div>
            <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className={`h-full ${color} rounded-full transition-all duration-500`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function MobileDISC() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [existingResult, setExistingResult] = useState(null);
  const [canRetake, setCanRetake] = useState(false);
  const [daysUntilRetake, setDaysUntilRetake] = useState(0);
  const [isRetaking, setIsRetaking] = useState(false);
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  // Carregar dados existentes do candidato
  useEffect(() => {
    const loadExistingDISC = async () => {
      try {
        const response = await candidatesAPI.getMe();
        const candidate = response.data;
        
        if (candidate.disc_detalhado && candidate.disc_updated_at) {
          // Verificar se pode refazer (7 dias)
          const lastUpdate = new Date(candidate.disc_updated_at);
          const now = new Date();
          const diffTime = now - lastUpdate;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          if (diffDays >= 7) {
            setCanRetake(true);
          } else {
            setDaysUntilRetake(7 - diffDays);
          }
          
          // Montar resultado existente
          const discData = candidate.disc_detalhado;
          const dominantTrait = discData.resultado_dominante?.charAt(0) || 'D';
          const secondaryTrait = discData.resultado_dominante?.charAt(1) || 'I';
          
          // Reconstruir scores se disponíveis ou estimar
          const scores = discData.scores || {
            D: dominantTrait === 'D' ? 20 : secondaryTrait === 'D' ? 17 : 12,
            I: dominantTrait === 'I' ? 20 : secondaryTrait === 'I' ? 17 : 12,
            S: dominantTrait === 'S' ? 20 : secondaryTrait === 'S' ? 17 : 12,
            C: dominantTrait === 'C' ? 20 : secondaryTrait === 'C' ? 17 : 12
          };
          
          setExistingResult({
            dominant: dominantTrait,
            secondary: secondaryTrait,
            scores: scores,
            aderencia: discData.aderencia_cargo || 80,
            updatedAt: candidate.disc_updated_at
          });
        }
      } catch (error) {
        console.error('Erro ao carregar DISC:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadExistingDISC();
  }, []);

  const totalQuestions = DISC_QUESTIONS.length;
  const progress = ((currentQuestion + (result ? 1 : 0)) / totalQuestions) * 100;
  const currentAnswer = answers[currentQuestion];

  const handleSelectAnswer = (value) => {
    setAnswers({ ...answers, [currentQuestion]: value });
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateResult();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateResult = () => {
    const scores = { D: 0, I: 0, S: 0, C: 0 };
    
    DISC_QUESTIONS.forEach((q, index) => {
      const answer = answers[index] || 3;
      scores[q.trait] += answer;
    });
    
    const sortedTraits = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const dominant = sortedTraits[0][0];
    const secondary = sortedTraits[1][0];
    
    const maxScore = 25;
    const dominantPercentage = Math.round((scores[dominant] / maxScore) * 100);
    
    setResult({
      dominant,
      secondary,
      scores,
      aderencia: dominantPercentage
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const profile = DISC_PROFILES[result.dominant];
      const discDetalhado = {
        resultado_dominante: `${result.dominant}${result.secondary}`,
        aderencia_cargo: result.aderencia,
        texto_explicativo: profile.description,
        pontos_fortes: profile.strengths,
        pontos_atencao: profile.attention,
        scores: result.scores // Salvar scores para gráfico
      };
      
      await candidatesAPI.updateMe({ 
        disc_profile: `${result.dominant}${result.secondary}`,
        disc_detalhado: discDetalhado
      });
      
      // Verificar se veio de um convite de teste e marcar como respondido
      const inviteToken = sessionStorage.getItem('test_invite_token');
      if (inviteToken) {
        try {
          await testInvitesAPI.complete(inviteToken);
          sessionStorage.removeItem('test_invite_token');
          toast.success('Teste DISC completado com sucesso! A empresa foi notificada.');
        } catch (inviteError) {
          console.error('Erro ao completar convite:', inviteError);
          // Não bloquear o fluxo se falhar a atualização do convite
        }
      } else {
        toast.success('Perfil DISC salvo com sucesso!');
      }
      
      navigate('/m/profile');
    } catch (error) {
      toast.error('Erro ao salvar perfil DISC');
    } finally {
      setSaving(false);
    }
  };

  const handleStartRetake = () => {
    setIsRetaking(true);
    setCurrentQuestion(0);
    setAnswers({});
    setResult(null);
    setExistingResult(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  // Mostrar resultado existente
  if (existingResult && !isRetaking && !result) {
    const profile = DISC_PROFILES[existingResult.dominant];
    const secondaryProfile = DISC_PROFILES[existingResult.secondary];
    const ProfileIcon = profile.icon;

    return (
      <div className="min-h-screen bg-slate-50 pb-24" data-testid="disc-existing-result">
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 px-4 pt-4 pb-20 rounded-b-3xl">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => navigate('/m')} className="text-slate-400">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-bold text-white">Seu Perfil DISC</h1>
          </div>

          <div className="text-center">
            <div className={`w-20 h-20 ${profile.bgColor} rounded-full flex items-center justify-center mx-auto mb-3`}>
              <ProfileIcon className={`w-10 h-10 ${profile.color}`} />
            </div>
            <h2 className="text-2xl font-bold text-white">{profile.title}</h2>
            <p className={`text-lg ${profile.color}`}>{profile.name}</p>
            <div className="mt-2 flex items-center justify-center gap-2">
              <span className="text-slate-400 text-sm">Secundário:</span>
              <span className={`${secondaryProfile.color} font-medium`}>{secondaryProfile.title}</span>
            </div>
            <div className="mt-3 flex items-center justify-center gap-2 text-slate-400 text-xs">
              <Clock className="w-4 h-4" />
              <span>Avaliado em {formatDate(existingResult.updatedAt)}</span>
            </div>
          </div>
        </div>

        <div className="px-4 -mt-12 space-y-4">
          {/* Gráfico Radar */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-4 text-center">Gráfico do Perfil</h3>
              <DISCRadarChart scores={existingResult.scores} />
            </CardContent>
          </Card>

          {/* Barras de Comparação */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-4">Detalhamento por Traço</h3>
              <DISCBars scores={existingResult.scores} />
            </CardContent>
          </Card>

          {/* Sobre o perfil */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-2">Sobre você</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{profile.description}</p>
            </CardContent>
          </Card>

          {/* Pontos Fortes */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-3">Seus Pontos Fortes</h3>
              <div className="flex flex-wrap gap-2">
                {profile.strengths.map((strength, i) => (
                  <span 
                    key={i}
                    className={`px-3 py-1 rounded-full text-sm ${profile.bgColor} ${profile.color}`}
                  >
                    {strength}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Pontos de Atenção */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-3">Pontos de Atenção</h3>
              <div className="flex flex-wrap gap-2">
                {profile.attention.map((item, i) => (
                  <span 
                    key={i}
                    className="px-3 py-1 rounded-full text-sm bg-amber-100 text-amber-700"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Dica */}
          <Card className="border-cyan-200 bg-cyan-50">
            <CardContent className="p-4">
              <h3 className="font-semibold text-cyan-900 mb-2">Dica de Desenvolvimento</h3>
              <p className="text-sm text-cyan-700">{profile.tips}</p>
            </CardContent>
          </Card>
        </div>

        {/* Botão de Refazer */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
          {canRetake ? (
            <Button 
              className="w-full bg-cyan-600 hover:bg-cyan-700"
              onClick={handleStartRetake}
              data-testid="disc-retake-btn"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Refazer Avaliação
            </Button>
          ) : (
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-slate-500 mb-2">
                <Clock className="w-4 h-4" />
                <span className="text-sm">
                  Você poderá refazer em <strong>{daysUntilRetake} dia{daysUntilRetake > 1 ? 's' : ''}</strong>
                </span>
              </div>
              <Button 
                variant="outline"
                className="w-full"
                disabled
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Refazer Avaliação
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Tela de resultado após questionário
  if (result) {
    const profile = DISC_PROFILES[result.dominant];
    const secondaryProfile = DISC_PROFILES[result.secondary];
    const ProfileIcon = profile.icon;

    return (
      <div className="min-h-screen bg-slate-50 pb-24" data-testid="disc-result">
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 px-4 pt-4 pb-20 rounded-b-3xl">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => navigate('/m')} className="text-slate-400">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-bold text-white">Seu Resultado DISC</h1>
            <CheckCircle2 className="w-5 h-5 text-emerald-400 ml-auto" />
          </div>

          <div className="text-center">
            <div className={`w-24 h-24 ${profile.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
              <ProfileIcon className={`w-12 h-12 ${profile.color}`} />
            </div>
            <h2 className="text-2xl font-bold text-white">{profile.title}</h2>
            <p className={`text-lg ${profile.color}`}>{profile.name}</p>
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="text-slate-400 text-sm">Perfil secundário:</span>
              <span className={`${secondaryProfile.color} font-medium`}>{secondaryProfile.name}</span>
            </div>
            <div className="mt-4 bg-white/10 rounded-full px-4 py-2 inline-block">
              <span className="text-white text-sm">Aderência: </span>
              <span className="text-cyan-400 font-bold">{result.aderencia}%</span>
            </div>
          </div>
        </div>

        <div className="px-4 -mt-12 space-y-4">
          {/* Gráfico Radar */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-4 text-center">Gráfico do Perfil</h3>
              <DISCRadarChart scores={result.scores} />
            </CardContent>
          </Card>

          {/* Barras */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-4">Detalhamento por Traço</h3>
              <DISCBars scores={result.scores} />
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-2">Sobre você</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{profile.description}</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-3">Seus Pontos Fortes</h3>
              <div className="flex flex-wrap gap-2">
                {profile.strengths.map((strength, i) => (
                  <span 
                    key={i}
                    className={`px-3 py-1 rounded-full text-sm ${profile.bgColor} ${profile.color}`}
                  >
                    {strength}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-3">Pontos de Atenção</h3>
              <div className="flex flex-wrap gap-2">
                {profile.attention.map((item, i) => (
                  <span 
                    key={i}
                    className="px-3 py-1 rounded-full text-sm bg-amber-100 text-amber-700"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-cyan-200 bg-cyan-50">
            <CardContent className="p-4">
              <h3 className="font-semibold text-cyan-900 mb-2">Dica de Desenvolvimento</h3>
              <p className="text-sm text-cyan-700">{profile.tips}</p>
            </CardContent>
          </Card>
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
          <Button 
            className="w-full bg-cyan-600 hover:bg-cyan-700"
            onClick={handleSave}
            disabled={saving}
            data-testid="disc-save-btn"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
            {saving ? 'Salvando...' : 'Salvar Perfil'}
          </Button>
        </div>
      </div>
    );
  }

  // Tela do questionário
  const question = DISC_QUESTIONS[currentQuestion];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="disc-quiz">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 border-b border-slate-200">
        <div className="flex items-center gap-3 p-4">
          <button onClick={() => navigate('/m')} className="text-slate-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-900">Teste DISC</h1>
            <p className="text-xs text-slate-500">Pergunta {currentQuestion + 1} de {totalQuestions}</p>
          </div>
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
            <Brain className="w-5 h-5 text-purple-600" />
          </div>
        </div>
        <Progress value={progress} className="h-1.5" />
        
        {/* Navigation Buttons - Moved to top */}
        <div className="flex gap-2 px-4 py-2 bg-slate-50 border-t border-slate-100">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className="flex-1 h-9"
            data-testid="disc-prev-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Anterior
          </Button>
          <Button
            size="sm"
            onClick={handleNext}
            disabled={currentAnswer === undefined}
            className="flex-1 h-9 bg-cyan-600 hover:bg-cyan-700"
            data-testid="disc-next-btn"
          >
            {currentQuestion === totalQuestions - 1 ? 'Ver Resultado' : 'Próximo'}
            {currentQuestion < totalQuestions - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>

      {/* Question */}
      <div className="p-4">
        <Card className="border-slate-200 mb-4">
          <CardContent className="p-4">
            <p className="text-base font-medium text-slate-900 text-center leading-relaxed">
              "{question.text}"
            </p>
          </CardContent>
        </Card>

        {/* Likert Scale - Compact version */}
        <div className="space-y-2">
          <p className="text-xs text-slate-500 text-center mb-2">
            O quanto você concorda com esta afirmação?
          </p>
          
          {LIKERT_SCALE.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSelectAnswer(option.value)}
              className={`w-full p-3 rounded-xl text-left transition-all ${
                currentAnswer === option.value
                  ? 'bg-cyan-600 text-white shadow-lg scale-[1.02]'
                  : 'bg-white border border-slate-200 hover:border-cyan-500 hover:bg-cyan-50'
              }`}
              data-testid={`disc-likert-${option.value}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  currentAnswer === option.value
                    ? 'bg-white/20 text-white'
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {option.value}
                </div>
                <span className={`text-sm font-medium ${
                  currentAnswer === option.value ? 'text-white' : 'text-slate-900'
                }`}>
                  {option.label}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Progress indicator */}
        <p className="text-center text-xs text-slate-400 mt-4">
          {Object.keys(answers).length} de {totalQuestions} respondidas
        </p>
      </div>
    </div>
  );
}
